/**
 * Resolving the PROJECT on a quote / invoice line, and the cities that follow.
 *
 * ## What this replaces
 *
 * A line used to take its city options from the DOCUMENT's project. That was
 * not a loose filter, it was a filter about something else: a project names
 * exactly one product and a quote sells many, so on any line whose product was
 * not the header project's product the picker was filtered against the wrong
 * project entirely. The project moved onto the line; this hook is the picker
 * that resolves it.
 *
 * The flow, and the order is the whole design:
 *
 * ```
 *   product  ─┐
 *   region   ─┼─►  GET /projects?productId=&regionId=&cycleId=&intakeId=&isActive=true
 *   cycle    ─┤          one   → select it silently
 *   intake   ─┘          many  → make the person choose
 *                        none  → say WHICH kind of none it is (see below)
 *                                   ▼
 *                            project.cities  ──►  the line's city multiselect
 * ```
 *
 * ## Two directions, and a caller picks ONE deliberately
 *
 * The flow above is **product-first**, and it is still the default.
 * `useAllLineProjects` adds the **project-first** direction the owner asked for
 * — *"either by project or product"* — and it is the arrow reversed, not a
 * variant of the same walk:
 *
 * ```
 *   project ─┬─►  its ONE product   ──►  the line's product
 *            └─►  project.cities    ──►  the line's city multiselect
 * ```
 *
 * **Why it is safe.** ⚠️ *SUPERSEDED, the reason: "A project names exactly one
 * product — `product_id` is `NOT NULL` on `d_projects` and there is no join
 * table."* **Migration 156 added `f_project_products` and a project sells
 * SEVERAL.** What survives is the conclusion and not the argument: `product_id`
 * is still `NOT NULL` as the PRIMARY, so a project still implies exactly one
 * product to SEED a blank line with, and {@link productOfProject} still returns
 * it with no ambiguity. There is nothing to disambiguate, no counterfactual to
 * run and no exemption question to ask, which is why project-first needs none of
 * the machinery product-first does. It is strictly the simpler half.
 *
 * ⚠️ **But NARROWING is a different question from SEEDING, and only the seed
 * takes the primary.** Asking whether a project may carry a line — the
 * product-first direction, and every grid's per-line picker — is
 * {@link projectSellsProduct}, over the whole SET. Answering it from the primary
 * hides a project that sells the line's product as a secondary, silently.
 *
 * **Where it is worse, and it is worse in exactly one place.** It cannot send
 * `?productId=`, so it pages over every active project in the business rather
 * than one product's handful. It is the call here that meets the 100-row cap
 * first. See "Paging".
 *
 * **Two answers project-first does not have, and they come back `null` rather
 * than `false`:** `productHasNoProjects` (no product was named, so the question
 * was never asked) and `matchesWithoutIntake` (no intake filter is applied, so
 * there is no counterfactual to report). A caller reading either as `false` is
 * reading an answer nobody gave. That is why they are `| null` in the first
 * place, and why both hooks return the same `LineProjectState`: the caller
 * switches direction, not shape.
 *
 * ## An empty result has TWO causes and they need two different sentences
 *
 * Both are answered here, because collapsing them has the same consequence in
 * each direction: somebody creates a project that should never have existed.
 *
 * | the fetch returned nothing because | what the UI must say |
 * |---|---|
 * | an **intake** was applied that excludes cycle-only projects | "no project matches with Fall 2026 selected; 2 match without it" — offer to drop it |
 * | the **product** genuinely has no projects | "this product is not sold through projects" — the line needs no project, and the city control is **absent**, not merely disabled |
 *
 * ## ⚠️ The intake trap — the first of those, and the reason this hook exists
 *
 * **A project MUST have a cycle (`cycle_id` is `NOT NULL`) but MAY have no
 * intake (`intake_id` is nullable).** A project with no intake spans the whole
 * of its cycle, which means it legitimately covers the intake the user just
 * picked — and `?intakeId=` does not match it, because the column is null.
 *
 * So `?intakeId=` does not narrow to "projects covering that window". It
 * narrows to "projects that happen to NAME that window", and silently drops
 * every project that covers it by spanning the whole cycle. A person who picks
 * an intake then reads *"No project covers that combination"* while two
 * perfectly good projects match without it. The predictable next move is not to
 * clear one box — nobody suspects the box — it is to go and create a
 * **duplicate project**, which is a permanent piece of bad data produced by a
 * sentence that was true of the filter and false of the world.
 *
 * Hence two rules here. **Intake narrows LAST**, after product, region and
 * cycle, so the set is already meaningful before it is applied. And when an
 * intake is applied and the result comes back empty, this asks the same
 * question again **without** it and reports `matchesWithoutIntake`. That second
 * request fires only when it can change the answer — an intake is applied AND
 * the narrowed result is empty — never speculatively.
 *
 * ## ⚠️ The exemption — the second of those, and it is NOT the same thing
 *
 * A line whose product is sold through projects must name one. But **some
 * products have no projects at all** — `Meet Up`, `Profile Evaluations` and
 * `Miscellaneous` are the live examples — and those are exempt: the line
 * carries no project, and naming cities on it gets its own server sentence
 * rather than "choose a project". `productHasNoProjects` is that answer, and it
 * is deliberately a separate field from an empty `projects`, because "nothing
 * matched these four filters" and "this product is not sold this way" are
 * different facts that happen to arrive as the same zero.
 *
 * ## The derivation is CONFIRMED against the server's own predicate
 *
 * There is no "is this product exempt" endpoint, so the only way to ask is a
 * fetch filtered by `productId` and `isActive=true`. That must stay
 * `isActive=true`: a product whose only project has been retired must be
 * exempt here too, because the picker only offers active projects, and a
 * refusal demanding a project the person cannot select is advice that cannot
 * be followed.
 *
 * The server's predicate is now literally this, and it is the definition rather
 * than a coincidence:
 *
 * ```sql
 * SELECT id FROM d_projects WHERE product_id = ? AND is_active = 1 LIMIT 1
 * ```
 *
 * The rule worth carrying: **a server-side predicate a client can only derive
 * one way has to be DEFINED the way the client can derive it**, or the two
 * answer differently for the same product and neither is obviously wrong.
 *
 * One honest consequence, so nobody meets it as a surprise: retiring the last
 * project for a product makes its lines project-less again. That is what
 * retiring it means, not a bug.
 *
 * ## Active only, and where the stored value is handled instead
 *
 * The fetch is always `?isActive=true`. A retired project is its own named
 * refusal on a line write (`Project {id} is no longer active`), so a picker
 * offering retired rows manufactures guaranteed 422s.
 *
 * **The division of labour:** this hook fetches what may be CHOSEN. A line that
 * already holds a since-retired project is the FORM's problem — it injects the
 * stored value back into the options, labelled, exactly as `projectOptions()`
 * does in `use-project-options.ts`. Nothing here injects it, because a hook that
 * both filters to active and quietly re-adds an inactive row has two rules and
 * no way to tell which one produced any given option.
 *
 * ## Paging
 *
 * `/projects` defaults to `perPage: 20` and caps at **100** — lower than the
 * masters, which go to 200. There is no "give me all", so this reports
 * `hasMore` rather than pretending the first page is the whole list. A picker
 * silently missing its 101st option is indistinguishable from that project not
 * existing, and the person who cannot find it has no way to tell which. In
 * practice a product-filtered list will rarely reach it; "rarely" is not
 * "never", and the failure is silent.
 *
 * ⚠️ **Project-first is where this actually bites.** `useAllLineProjects` sends
 * no `productId`, so it pages over the whole active list — six rows today, and
 * the first of these calls that will cross a hundred. It will do it silently and
 * on a screen where the missing row is the ONE thing the person came to pick.
 *
 * So it takes an optional `search`, and that is a real escape from the cap
 * rather than a convenience: `hasMore` tells someone a project might be missing,
 * and `search` is the only way they can go and find it. **A caller that renders
 * the flag without offering the search has announced a problem and withheld the
 * fix.** `search` matches `name` only — not the product, not the description —
 * so a person hunting by product name will find nothing and must switch to the
 * product-first direction, which is what it is for.
 */

import { useEffect, useMemo, useState } from 'react';
import { ApiError, api } from './api';
import {
  UNKNOWN_CYCLE_SCOPES,
  cycleScopeIdIndex,
  type CycleScopeIdIndex,
} from './project-cycle-scope';
import { useCycleOptions } from './use-cycle-options';
import type { MasterRef, Project, ProjectCity, ProjectListParams } from './types';

/** The endpoint's ceiling. 101 is a 422, and the default of 20 is not enough. */
const PER_PAGE = 100;

/**
 * A count is all a probe wants — `meta.total` is exact on this endpoint and the
 * rows are never rendered — so it asks for the smallest legal page. `perPage: 0`
 * is a 422; the floor is 1.
 */
const PROBE_PER_PAGE = 1;

/**
 * Does this product have any active project at all? Keyed by `productId`.
 *
 * Cached because the answer decides whether a whole control is on the screen,
 * and re-asking it on every change of the region / cycle / intake pickers would
 * spend a request per keystroke to be told the same thing. Module-level so it
 * survives a line editor closing and reopening, which is exactly the loop a
 * person filling in a document repeats.
 *
 * Staleness is bounded rather than ignored: any fetch that comes back non-empty
 * for a product overwrites the entry with `true`, so a project created during
 * the session corrects the cache the moment it is matched. A stale `false` can
 * therefore only persist while the filtered fetch is also empty. A reload
 * clears it entirely.
 */
const productHasProjectsCache = new Map<number, boolean>();

export interface LineProjectFilters {
  productId: number | null;
  regionId: number | null;
  cycleId: number | null;
  intakeId: number | null;
}

export interface LineProjectState {
  /** Active projects matching the current filters. */
  projects: Project[];
  loading: boolean;
  /** A fetch failure, already worded for a person. null when fine. */
  error: string | null;
  /** True when the 100-row cap hid matches — a picker silently missing its 101st option is indistinguishable from that project not existing. */
  hasMore: boolean;
  /**
   * How many projects match if the INTAKE filter were dropped.
   * `null` when no intake is applied, or when the narrowed result is non-empty
   * (nothing to offer). A number > 0 means the UI must offer to drop the intake.
   */
  matchesWithoutIntake: number | null;
  /**
   * True when the PRODUCT has no projects at all — not when the other filters
   * narrowed to nothing. The line then needs no project and names no cities.
   * `null` while unknown (not yet fetched / still loading).
   */
  productHasNoProjects: boolean | null;
}

/**
 * Builds the list query for one step of the picker.
 *
 * Every key is omitted when it is null rather than sent empty. The `/projects`
 * list query is `.strict()` and **an empty value is a 422, not "no filter"** —
 * `?productId=` coerces to 0, so `.default()` never fires and `.positive()`
 * rejects it. Passing a params object to `api.listProjects` routes it through
 * `listQuery()`, which drops undefined, null and `''`; a hand-built query
 * string is how the bare `key=` pairs get out.
 */
function paramsFor(
  filters: LineProjectFilters,
  include: { region: boolean; cycle: boolean; intake: boolean },
  perPage: number,
): ProjectListParams {
  const params: ProjectListParams = {
    page: 1,
    perPage,
    // Never omitted. See the header: a retired project is a named refusal on save.
    isActive: true,
    sort: 'name',
    order: 'asc',
  };
  if (filters.productId !== null) params.productId = filters.productId;
  if (include.region && filters.regionId !== null) params.regionId = filters.regionId;
  if (include.cycle && filters.cycleId !== null) params.cycleId = filters.cycleId;
  if (include.intake && filters.intakeId !== null) params.intakeId = filters.intakeId;
  return params;
}

const ALL = { region: true, cycle: true, intake: true } as const;
const WITHOUT_INTAKE = { region: true, cycle: true, intake: false } as const;
const PRODUCT_ONLY = { region: false, cycle: false, intake: false } as const;

/**
 * A failure the person reading it can act on.
 *
 * `formMessage` first because a 422 here means this app sent a filter the
 * strict query schema refused, and the offending key is named only in there —
 * the top-level message on a 422 is the generic "Validation failed".
 */
function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) return caught.formMessage ?? caught.message;
  if (caught instanceof Error && caught.message !== '') return caught.message;
  return 'Could not load the projects for this line.';
}

/** Everything one completed run produced, stamped with the filters it answers. */
interface Snapshot extends Omit<LineProjectState, 'loading'> {
  /**
   * The question this run answers, so a stale run is never rendered: the four
   * filter values product-first, the search term project-first. Both hooks
   * compare it against the question being asked at render time rather than
   * resetting state on the way in — see `useLineProject` for why.
   */
  key: string;
}

/**
 * The identity of a filter combination.
 *
 * Nulls are part of it: "cycle 5, no intake" and "cycle 5, intake 8" are
 * different questions with legitimately different answers, and a key that
 * dropped the empty ones would answer the second with the first.
 */
function filterKey(filters: LineProjectFilters): string {
  return [filters.productId, filters.regionId, filters.cycleId, filters.intakeId].join('|');
}

const IDLE = {
  projects: [] as Project[],
  error: null,
  hasMore: false,
  matchesWithoutIntake: null,
  productHasNoProjects: null,
} satisfies Omit<LineProjectState, 'loading'>;

/**
 * The project picker for one line.
 *
 * `filters` is read field by field, so a caller may pass a fresh object literal
 * every render without refetching — the effect depends on the four numbers, not
 * on the object's identity.
 *
 * **Nothing is fetched with no product.** With no product there is nothing to
 * narrow: the whole project list would be noise, and every row in it would be
 * the wrong-product refusal on save. `enabled` is the caller's own gate on top
 * of that — a closed line editor should not be reading the list.
 *
 * One run publishes ONE snapshot, stamped with the filters it answers, and
 * everything the caller sees is derived from it during render. That is why
 * there is no `setLoading(true)` and no bank of resets at the top of the
 * effect: a snapshot for a different product simply stops matching, so stale
 * rows can never be rendered for the current one — and a picker still showing
 * the previous product's projects is worse than an empty one, because every
 * option in it saves to a refusal whose cause is not on the screen.
 */
export function useLineProject(
  filters: LineProjectFilters,
  enabled: boolean,
): LineProjectState {
  const { productId, regionId, cycleId, intakeId } = filters;
  const active = enabled && productId !== null;
  const key = filterKey(filters);

  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);

  useEffect(() => {
    if (!active || productId === null) return;

    const controller = new AbortController();
    const { signal } = controller;
    const current: LineProjectFilters = { productId, regionId, cycleId, intakeId };
    const runKey = filterKey(current);

    const publish = (result: Omit<LineProjectState, 'loading'>) => {
      if (!signal.aborted) setSnapshot({ key: runKey, ...result });
    };
    const remember = (value: boolean) => {
      productHasProjectsCache.set(productId, value);
      return value;
    };

    /*
     * `await` rather than a `.then` chain, because the empty case asks up to
     * two follow-up questions whose answers depend on each other. The guard the
     * house rule is really about is unchanged and applies at every suspension
     * point: nothing touches state after `signal.aborted`, in the happy path or
     * in a catch.
     */
    void (async () => {
      let projects: Project[];
      let hasMore: boolean;
      try {
        const result = await api.listProjects(paramsFor(current, ALL, PER_PAGE), signal);
        if (signal.aborted) return;
        projects = result.data;
        /*
         * `total` against what arrived, not `totalPages > 1`: `meta.totalPages`
         * is 0 — not 1 — for an empty set, so anything reading it as a page
         * count has an off-by-one waiting on the empty case.
         */
        hasMore = result.meta.total > projects.length;
      } catch (caught: unknown) {
        if (signal.aborted) return;
        publish({ ...IDLE, error: messageFor(caught) });
        return;
      }

      if (projects.length > 0) {
        // A match for this product is proof the product is sold through
        // projects. No probe, and the cache is corrected on the way past.
        remember(true);
        publish({ ...IDLE, projects, hasMore, productHasNoProjects: false });
        return;
      }

      // ---- Empty. Which of the two kinds of empty is it? ----

      const narrowedByOthers = regionId !== null || cycleId !== null;
      let hasProjects: boolean | undefined = productHasProjectsCache.get(productId);
      let matchesWithoutIntake: number | null = null;

      if (!narrowedByOthers && intakeId === null) {
        /*
         * Nothing but the product was applied, and it came back empty — so the
         * primary fetch WAS the exemption probe. Answered for free.
         */
        publish({ ...IDLE, hasMore, productHasNoProjects: !remember(false) });
        return;
      }

      if (intakeId !== null) {
        /*
         * The counterfactual. Reachable only with an intake applied and nothing
         * matching, so it costs a request exactly when it can change what the
         * screen says. When region and cycle are also unset this query is the
         * product-only probe as well, and answers both questions at once.
         */
        try {
          const wider = await api.listProjects(
            paramsFor(current, WITHOUT_INTAKE, PROBE_PER_PAGE),
            signal,
          );
          if (signal.aborted) return;
          matchesWithoutIntake = wider.meta.total;
          if (wider.meta.total > 0) hasProjects = remember(true);
          else if (!narrowedByOthers) hasProjects = remember(false);
        } catch {
          /*
           * Swallowed on purpose. The primary answer stands; all that is lost
           * is the offer to widen, and `null` says exactly that. Reporting a
           * failure of the second request as a failure of the first would blank
           * a list that loaded fine.
           */
          if (signal.aborted) return;
        }
      }

      if (hasProjects === undefined) {
        /*
         * The exemption probe: product and active, nothing else. The
         * `isActive=true` half matches the server's own predicate exactly —
         * `product_id = ? AND is_active = 1` — so a zero here IS the exemption,
         * not a proxy for it. See the header for why that is a definition
         * rather than a coincidence.
         */
        try {
          const anyForProduct = await api.listProjects(
            paramsFor(current, PRODUCT_ONLY, PROBE_PER_PAGE),
            signal,
          );
          if (signal.aborted) return;
          hasProjects = remember(anyForProduct.meta.total > 0);
        } catch {
          /*
           * Left unknown rather than guessed. `false` would tell the user the
           * product is sold through projects when nobody checked, and `true`
           * would hide a control they need; `null` renders neither claim.
           */
          if (signal.aborted) return;
        }
      }

      publish({
        ...IDLE,
        hasMore,
        matchesWithoutIntake,
        productHasNoProjects: hasProjects === undefined ? null : !hasProjects,
      });
    })();

    return () => controller.abort();
  }, [active, productId, regionId, cycleId, intakeId]);

  /*
   * A snapshot answering different filters is not this question's answer, so it
   * reads as "still loading" rather than being shown. This is also what makes
   * `loading` honest across the follow-up probes: one run publishes once, at
   * the end, so the screen never says "no project matches" in the moment before
   * it knows which sentence that is.
   */
  const fresh = active && snapshot?.key === key ? snapshot : null;

  return {
    ...(fresh ?? IDLE),
    loading: active && fresh === null,
  };
}

/**
 * The city options for a line: the cities its resolved project declares.
 *
 * **Never `/cities`.** That is the full master, and the server refuses every id
 * the line's project does not declare — the refusal names each offending id.
 * The project rows this hook already holds carry their own `cities`, so there is
 * nothing further to fetch.
 *
 * Returns `[]` for no project and for a project not in the current result. `[]`
 * is honest in both cases: the cities of a project that is not here are not
 * known here, and showing the previously selected project's would be a lie on a
 * document a customer receives.
 *
 * What the caller does with `[]` differs by reason, and that is the split
 * `productHasNoProjects` exists to make: a line whose product is sold through
 * projects but has none chosen yet should **disable** the multiselect until one
 * resolves — cities named on a line with no project is its own refusal, and
 * disabling means it never fires — while a line whose product has no projects at
 * all should not show a city control at all.
 */
export function citiesOfLineProject(
  projects: Project[],
  projectId: number | null,
): ProjectCity[] {
  if (projectId === null) return [];
  return projects.find((project) => project.id === projectId)?.cities ?? [];
}

/* -------------------------------------------------------------------------- */
/* Project-first: pick the project, and it names the product                   */
/* -------------------------------------------------------------------------- */

/**
 * Every active project, for a **direct** pick — the project-first direction.
 *
 * Unfiltered by product on purpose: that is the whole point of this direction.
 * Somebody who knows which run they are selling should not have to name its
 * product first in order to be offered it. Choosing here resolves the product
 * rather than the other way round — `productOfProject` below is the second half,
 * and `citiesOfLineProject` serves both directions unchanged.
 *
 * **`enabled` is the caller's gate and there is no second one.** Product-first
 * refuses to fetch with no product, because an unnarrowed list would be noise
 * and every row in it the wrong-product refusal on save. Here the unnarrowed
 * list IS the answer, so nothing else holds the request back — which means this
 * must not be mounted behind a closed line editor. It is a full-list read per
 * open.
 *
 * ## `search` is how somebody gets past the cap
 *
 * `/projects` caps `perPage` at 100 with no "give me all", and this is the call
 * that reaches it first (see the header). `search` is trimmed and omitted when
 * empty — never sent as `?search=` — and it matches the project's **`name`
 * only**, not its product and not its description. **Debounce it in the
 * caller**, at the house 250ms: every distinct term is one request.
 *
 * The term is part of the snapshot key, so rows fetched for a previous term are
 * never rendered against the current one. While a new term is in flight the hook
 * reads `loading` rather than showing the old list — a picker showing stale rows
 * beneath a query they do not match is how somebody picks the wrong project.
 *
 * ## What a caller may and may not read off these rows
 *
 * They are whole `Project` records, so three of their fields carry traps that
 * belong to the renderer rather than to this hook:
 *
 * - **`capacity`: `0` and `null` are different values and NEITHER is enforced.**
 *   `null` is uncapped, `0` is sold out. Never `?? 0`, and never render either
 *   as a limit — nothing anywhere refuses a line that exceeds it, by the owner's
 *   explicit decision rather than by omission.
 * - **`region` is the project's own field**, `null` meaning unclassified.
 *   ⚠️ Never derive it from `cities[].country`: `f_countries.region_id` is an
 *   unrelated upstream id, and since migration 047 that join returns India
 *   correctly and Egypt as "South East Asia". Quietly wrong is worse than
 *   obviously wrong, because the absurd version is the one somebody catches.
 * - **`products` is the SET and `products[0]` is the PRIMARY**, first by the
 *   read's own `ORDER BY` rather than by insertion order. See
 *   `productOfProject`, which seeds a blank line from it.
 *
 * ## The two fields this direction cannot answer
 *
 * `productHasNoProjects` and `matchesWithoutIntake` are **always `null`** here,
 * and `null` is the honest value rather than a stub: no product was named, so
 * the exemption question was never asked; no intake filter was applied, so there
 * is no counterfactual to report. A caller must not read `null` as `false`. The
 * exemption affordance and the "2 match without it" affordance both belong to
 * `useLineProject`, and neither has any meaning in this direction.
 */
export function useAllLineProjects(enabled: boolean, search = ''): LineProjectState {
  const term = search.trim();

  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();
    const { signal } = controller;

    /*
     * Built key by key and handed to `listQuery()`, never assembled as a string.
     * `/projects` is `.strict()` and an EMPTY value is a 422, not "no filter".
     * `?search=` happens to be the one key that validates and is then ignored as
     * falsy — relying on that would be relying on the single exception.
     */
    const params: ProjectListParams = {
      page: 1,
      perPage: PER_PAGE,
      // Never omitted. See the header: a retired project is a named refusal on save.
      isActive: true,
      sort: 'name',
      order: 'asc',
    };
    if (term !== '') params.search = term;

    api
      .listProjects(params, signal)
      .then((result) => {
        if (signal.aborted) return;
        /*
         * A project that came back ACTIVE for a product is proof that product is
         * sold through projects, so the exemption cache is corrected on the way
         * past — exactly as the product-first path does with its own result.
         *
         * It may only ever write `true` from here. An empty result, especially
         * under a search term, says nothing whatever about any product, and a
         * `false` written off it would hide a control the line genuinely needs.
         *
         * ⚠️ **EVERY product the project sells, not just the primary** (migration
         * 156). The probe this cache short-circuits is `?productId=`, a
         * server-side `EXISTS` over the join, so marking the primary alone made
         * the cache answer a NARROWER question than the thing it stands in for.
         * It under-populated rather than lying — a miss falls through to the
         * probe — so the cost was a redundant request, never a wrong control.
         */
        for (const project of result.data) {
          for (const product of project.products ?? (project.product ? [project.product] : [])) {
            productHasProjectsCache.set(product.id, true);
          }
        }
        setSnapshot({
          ...IDLE,
          key: term,
          projects: result.data,
          /*
           * `total` against what arrived, never `totalPages > 1`:
           * `meta.totalPages` is 0 — not 1 — for an empty set, so anything
           * reading it as a page count has an off-by-one waiting on that case.
           */
          hasMore: result.meta.total > result.data.length,
        });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setSnapshot({ ...IDLE, key: term, error: messageFor(caught) });
      });

    /*
     * No `.finally`, and nothing for one to guard: `loading` is derived from the
     * snapshot's key at render rather than held in state. That is the same
     * arrangement `useLineProject` uses, and it is why neither hook can leave a
     * spinner running after an abort.
     */
    return () => controller.abort();
  }, [enabled, term]);

  const fresh = enabled && snapshot?.key === term ? snapshot : null;

  return {
    ...(fresh ?? IDLE),
    loading: enabled && fresh === null,
  };
}

/**
 * The product a project implies. **A project sells SEVERAL; this returns the
 * PRIMARY, deliberately.**
 *
 * 🔴 **CORRECTED 2026-09-14.** *SUPERSEDED: "**A project names exactly one.**
 * That is what makes project-first safe: `d_projects.product_id` is `NOT NULL`
 * and **there is no join table**, so there is one answer and no rule is needed
 * to choose between candidates."* **Migration 156 added `f_project_products`
 * two months ago.** A project sells several, and the read serves both the
 * deprecated singular `product` and the plural `products`.
 *
 * ⚠️ **THE FUNCTION IS UNCHANGED AND STILL CORRECT, WHICH IS EXACTLY WHY THE
 * PARAGRAPH SURVIVED.** Project-first seeds the PRIMARY — that is what the
 * three line grids' hints promise and what picking a project actually does — so
 * nothing ever failed to reveal that the argument underneath had died. Right
 * answer, dead reason, in the file the grids point at as their justification: a
 * reader checking why they may read the primary landed on a paragraph arguing
 * from the absence of a join table that now exists.
 *
 * **The live justification is narrower and is this:** a project-first pick has
 * to put ONE product in the item box, and the primary is the one the server
 * re-asserts into the set on every write. Choosing among the others would be a
 * rule nobody has written; listing them all would describe something the pick
 * does not do. Selecting a project therefore sets the line's product outright.
 *
 * ✅ **IT READS `products[0]`, AND THAT IS THE PRIMARY BY AN API GUARANTEE —
 * corrected 2026-09-18.** ⚠️ *SUPERSEDED: "🔴 **AND IT DEPENDS ON A DEPRECATED
 * KEY WITH NO REPLACEMENT.** `products` is ordered primary-first, so
 * `products[0]` agrees with `product` today — **that is an ORDERING, not a
 * guarantee** … **There is no non-deprecated way to ask a project for its
 * primary product.** Until the API offers one, this function and the three grid
 * hints are why the backend cannot withdraw the singular."*
 *
 * **Settled by measuring the query rather than by re-reading a third document.**
 * `findProducts` in the backend's `projects.repository.ts` orders
 * `(pp.product_id <> pj.product_id), pr.product_name, pp.product_id`. The first
 * term is `0` for the row whose product IS `d_projects.product_id` and `1` for
 * every other one, so the primary leads the array **by the statement**, not by
 * the order rows happened to be inserted in. That repository's own heading reads
 * *"THE PRIMARY SORTS FIRST, AND THAT IS AN API GUARANTEE"*, and says it exists
 * so that *"a client migrating from one to the other cannot be moved"*. It was
 * written for this migration.
 *
 * ⚠️ **So this was never the proxy-field shape, and calling it that is what kept
 * the singular alive for nine days.** That shape is a field consulted for a
 * question nobody wrote it to answer; `products[0]` is written to answer exactly
 * this one. `types.ts` said so all along — two artefacts in this repo disagreed,
 * **nothing failed while they did**, and the pessimistic half was the one that
 * blocked work.
 *
 * ## ⚠️ It never guesses, and `null` is the whole safety property
 *
 * `null` has three causes, and every one of them means *do not set a product*:
 *
 * | | |
 * |---|---|
 * | `projectId` is null | nothing is chosen yet |
 * | the project is not in `projects` | a stale id, a different search term, or a row past the 100-row cap — its product is simply not known **here** |
 * | `products` is empty | it should not be — `d_projects.product_id` is `NOT NULL` and `ensurePrimaryProduct` re-asserts it into the set on every write — so an empty array means a server predating migration 156, and a guess is exactly as wrong there as anywhere else |
 *
 * A plausible default in any of those cases — the first project's product, the
 * line's existing one, the header's — silently changes **what the customer is
 * being sold**, on a document they are invoiced against. Nothing downstream can
 * catch it, because the line looks entirely ordinary afterwards. So on `null` a
 * caller leaves the product alone and says the project has not resolved; it does
 * not fill anything in.
 *
 * ## ⚠️ It returns a `MasterRef`, and the spelling is the reason
 *
 * `products[0]` is a `ProjectProductRef` — `{ id, name, seedsLineItems }`. A
 * LINE's product is a `ProductSummary` — `{ id, productCode, productName }` —
 * two spellings of one concept, flagged in `types.ts` as an artifact of two
 * modules typed by different hands rather than as anything meaningful. **This
 * does not paper over it.** Returning a `ProductSummary` would mean inventing a
 * `productCode` the project row does not carry, and a fabricated code on a
 * picker is a code somebody eventually reads out.
 *
 * So a caller takes `.id` to write `productId` — the only field a line write
 * actually needs — and `.name` for a label. It cannot be assigned into a
 * `ProductSummary` slot, and `tsc` saying so is the point of not widening it.
 *
 * ⚠️ **It narrows to `MasterRef` and deliberately does NOT carry
 * `seedsLineItems` onward.** That flag decides whether the company "+" pulls a
 * product into a NEW document's lines; this answers what goes in the item box of
 * a line somebody is editing by hand, which the flag has never governed — and
 * all three callers take `.id` alone. ⛔ **It is not a licence to annotate
 * `Project.products` as `MasterRef[]`**: that erases the flag STATICALLY while
 * the runtime objects still carry it, nothing fails, and the next reader strips
 * a field that appears unused. This repo has shipped that defect once already.
 */
export function productOfProject(
  projects: Project[],
  projectId: number | null,
): MasterRef | null {
  if (projectId === null) return null;
  return projects.find((project) => project.id === projectId)?.products[0] ?? null;
}

/**
 * Does this project sell this product? **The ONE copy of the picker's narrowing
 * rule**, and the inverse of {@link productOfProject} above.
 *
 * ## ⚠️ It reads `products`, the SET — never `product`, the primary
 *
 * Migration 156 made `f_project_products` a real join table: a project sells
 * SEVERAL products, `product` is the deprecated primary kept `NOT NULL` for the
 * expand half of an expand/contract, and `products[0]` is that same primary by
 * the read's `ORDER BY` rather than by luck. Matching the primary alone excludes
 * a project that sells the line's product as a SECONDARY — which is precisely
 * the case the migration was built for: **a travel line under a festival
 * project.** The exclusion is silent. A legitimate project is simply absent from
 * the list, which reads as a project nobody has set up.
 *
 * ## Why it is a shared function rather than a predicate written three times
 *
 * The quote, invoice and deal grids each narrow their per-line project picker,
 * and all three were written with the primary-only test in one commit. Three
 * copies agree the day they are written and drift on the first correction — and
 * a wrong answer here HIDES a project rather than refusing one, so there is
 * nothing on screen for the drift to show up in.
 *
 * ## The `products ?? [product]` fallback is for an OLDER server only
 *
 * `Project.products` is required in `types.ts` and migration 156 is live, so on
 * any response this app can see today the fallback is dead. It is here because
 * this client deploys separately from the API and could be pointed at a build
 * that predates 156, where `products` is absent and `.some` on `undefined`
 * throws inside a render — which React escalates to unmounting the tree, so a
 * missing column would blank the whole grid. Falling back to the primary
 * restores exactly the pre-156 behaviour instead. **It is not a licence to read
 * `product` anywhere else.**
 */
export function projectSellsProduct(project: Project, productId: number): boolean {
  const sold = project.products ?? (project.product ? [project.product] : []);
  return sold.some((product) => product.id === productId);
}

/* ── Which cycles a line's project picker offers, per PILL ──────────────────
 *
 * Owner's request, 9 September 2026: the project dropdown on a line item should
 * offer the CURRENT cycle and the ones ahead of it, not cycles that have
 * closed. Measured that day: 68 active projects, of which 36 sit in a finished
 * cycle — so more than half of every line picker in the app was history nobody
 * could legitimately sell against.
 *
 * ⚠️ **SUPERSEDED IN SHAPE, NOT IN RULE — 11 September 2026.** *SUPERSEDED:
 * `useOpenCycleIds(): Set<number> | null` and
 * `projectCycleIsOpen(project, openCycleIds)`.* The owner then asked for
 * **Current / Previous pills, Current by default** — so the single hard-wired
 * filter became a two-way choice whose `current` half is byte-for-byte the old
 * rule. **The predicate moved to `src/lib/project-cycle-scope.ts`** and both old
 * names were DELETED rather than left exported: two implementations of one
 * cycle rule drift, and a wrong answer here HIDES a project rather than
 * refusing one, so there is nothing on screen for the drift to show up in.
 *
 * ⚠️ **THIS NARROWS WHAT IS OFFERED. IT MUST NEVER NARROW WHAT IS RESOLVED.**
 * 113 line items already point at a project whose cycle has closed (31 quote,
 * 82 invoice, measured on production 2026-09-09). Those are historical
 * documents, and a picker that cannot show the value its own row holds is the
 * defect this codebase names against the company stage editor: the control
 * renders blank over a real value and the next save silently repoints the line.
 *
 * So the grids keep fetching every active project and keep RESOLVING against
 * that full list — which is what keeps an old line's cities and regions
 * working — and filter only the array they turn into options. A saved project
 * that falls outside the offer is then re-injected, under EVERY pill, by the
 * same block that already handles a since-retired one.
 */

/**
 * Both pills' cycle-id sets, read once for a whole grid.
 *
 * ⚠️ **A `null` member IS NOT "nothing is in this scope", AND EVERY CALLER MUST
 * TREAT IT AS "OFFER EVERYTHING".** A failed or unfinished `/cycles` read
 * resolving to an empty set would empty every project dropdown in the app, and
 * a picker with no options reads as *there are no projects* — a confident
 * statement about the data made on the strength of a request that never came
 * back. Degrading OPEN restores exactly the behaviour that shipped before any
 * of this existed, which is a longer list rather than an unusable one.
 *
 * Four separate ways to be unable to say, and each yields `null` rather than a
 * set that happens to be empty:
 *
 * | | why it is not an answer |
 * | --- | --- |
 * | `loading` | nothing has arrived yet |
 * | `failed` | the read did not come back |
 * | `hasMore` | the page is truncated, so a cycle absent from it is not proven closed |
 * | no rows | an empty master cannot classify anything (handled in `cycleScopeIdIndex`) |
 *
 * ⚠️ **Read ONCE per grid, not once per row.** It is a `/cycles` fetch behind
 * `useCycleOptions`, so a hook per row is a request per row of a table against
 * the office's shared per-IP budget — the same reason `useActiveProjects` is
 * hoisted. ⚠️ *SUPERSEDED: "the office's shared **120/60s** budget".* It is
 * **12,000/60s** (corrected 2026-09-17); the objection is the per-row SHAPE and
 * not the sum, and it is unchanged by the headroom. The *pill* is per row; the
 * *data* is not.
 *
 * ⚠️ **It ARRIVES LATE — `UNKNOWN_CYCLE_SCOPES` on first paint, a real index
 * once `/cycles` lands — so every memo that filters with it must NAME IT AS A
 * DEPENDENCY.** That was the real bug in the predecessor change, and lint caught
 * it where review did not: a memo that omits it computes the unfiltered list
 * once and never recomputes, leaving the whole feature inert with nothing
 * failing and nothing on screen to show it.
 */
export function useCycleScopeIds(enabled = true): CycleScopeIdIndex {
  const { cycles, loading, failed, hasMore } = useCycleOptions(enabled);

  return useMemo(() => {
    if (!enabled || loading || failed || hasMore) return UNKNOWN_CYCLE_SCOPES;
    /*
     * Both scopes from ONE reading of "today", which is why this is a single
     * call rather than two. Two calls straddling midnight could file the
     * turning-over cycle under neither pill, and a project reachable through no
     * pill has silently vanished from the app.
     */
    return cycleScopeIdIndex(cycles);
  }, [enabled, cycles, loading, failed, hasMore]);
}
